import { type EntitySet, type ODataCustomReport, entitySetValues } from "./types.ts";

const NonQueryableEntitySets = [
    "bulkOperations",
    "dynamicGroups",
    "reviewTasks",
    "reviewAnswers",
    "reportShareRecipients",
    "reviewUsers",
    "tenantSettings",
    "workspaceReviews"
] as const satisfies EntitySet[];

export type QueryableEntitySet = Exclude<EntitySet, (typeof NonQueryableEntitySets)[number]>;

export const queryableEntitySetValues = entitySetValues.filter(
    x => !(NonQueryableEntitySets as EntitySet[]).includes(x)
) as QueryableEntitySet[];

export function isQueryableEntitySet(value: string): value is QueryableEntitySet {
    return (queryableEntitySetValues as string[]).includes(value);
}

export function toQueryableEntitySet(value: string): QueryableEntitySet {
    if (!isQueryableEntitySet(value)) {
        throw new Error(`"${value}" is not a queryable entity set.`);
    }

    return value;
}

const NonExportableEntitySets = [
    "automations",
    "customReports",
    "reportShares",
    "reviews",
    "reviewAnswerSharedLinks",
    "reviewAnswerSharedLinkRecipients",
    "reviewAnswerMembership"
] as const satisfies QueryableEntitySet[];

export type ExportableEntitySet = Exclude<QueryableEntitySet, (typeof NonExportableEntitySets)[number]>;

export const exportableEntitySetValues = queryableEntitySetValues.filter(
    x => !(NonExportableEntitySets as QueryableEntitySet[]).includes(x)
) as ExportableEntitySet[];

const AutomationEntitySets = ["sharedLinks", "workspaces", "oneDrives"] as const satisfies QueryableEntitySet[];

export type AutomationEntitySet = (typeof AutomationEntitySets)[number];

export const automationEntitySetValues = AutomationEntitySets;

export interface QueryableODataCustomReport extends Omit<ODataCustomReport, "entitySet"> {
    entitySet: QueryableEntitySet;
}

export function isQueryableODataCustomReport(report: ODataCustomReport): report is QueryableODataCustomReport {
    return queryableEntitySetValues.includes(report.entitySet as QueryableEntitySet);
}

// Three overlapping entity-set lists, each answering a different question and deliberately not derived from one another:
// - queryableEntitySetValues (above): entity sets surfaced as user-facing reports.
// - EntitySetsWithoutODataEndpoint (below): which entity sets the BFF exposes no `/bff/odata/{entitySet}` route for.
//   Not derived from queryableEntitySetValues: `tenantSettings` has an endpoint but is not a report.
// - ODataQueryEntitySets (below): the subset that additionally has a POST `/bff/odata/{entitySet}/$query` route,
//   which gates cross-page selection via isODataQueryEntitySet (apps/tenant-assessment/src/components/odata/ODataReport.tsx).
//   Entities outside it still read fine over GET; they just get selection disabled.
const EntitySetsWithoutODataEndpoint = [
    "dynamicGroups",
    "reportShareRecipients",
    "reviewTasks",
    "reviewAnswers",
    "reviewUsers",
    "workspaceReviews"
] as const satisfies EntitySet[];

export type ODataEndpointEntitySet = Exclude<EntitySet, (typeof EntitySetsWithoutODataEndpoint)[number]>;

export function hasODataEndpoint(value: EntitySet): value is ODataEndpointEntitySet {
    return !(EntitySetsWithoutODataEndpoint as readonly EntitySet[]).includes(value);
}

const ODataQueryEntitySets = [
    "archivedSiteCollections",
    "documentLibraries",
    "groupPrincipals",
    "groups",
    "oneDrives",
    "sharedLinks",
    "siteCollections",
    "userLicenses",
    "users",
    "workspaces"
] as const satisfies QueryableEntitySet[];

export type ODataQueryEntitySet = (typeof ODataQueryEntitySets)[number];

export const odataQueryEntitySetValues: readonly ODataQueryEntitySet[] = ODataQueryEntitySets;

export function isODataQueryEntitySet(value: string): value is ODataQueryEntitySet {
    return (odataQueryEntitySetValues as readonly string[]).includes(value);
}
